'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  MapPin,
  Calendar,
  ExternalLink,
  HandHeart,
  GraduationCap,
  Shield,
} from 'lucide-react';
import type { SmartRegistration, SmartGroup, CampusSession } from '@/types/serve-finder';

interface RetreatFlowProps {
  registration: SmartRegistration;
}

type Intent = 'student' | 'leader' | 'serving';
type Step = 'intent' | 'group' | 'campus' | 'result';

const intentConfig = {
  student: {
    label: 'Student',
    description: "I'm attending as a student",
    Icon: GraduationCap,
  },
  leader: {
    label: 'Leader',
    description: "I'm attending as an adult leader",
    Icon: Shield,
  },
  serving: {
    label: 'Work Crew',
    description: 'I want to serve behind the scenes',
    Icon: HandHeart,
  },
} as const;

export function RetreatFlow({ registration }: RetreatFlowProps) {
  const [step, setStep] = useState<Step>('intent');
  const [intent, setIntent] = useState<Intent | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<SmartGroup | null>(null);
  const [selectedCampus, setSelectedCampus] = useState<CampusSession | null>(null);

  const reset = () => {
    setStep('intent');
    setIntent(null);
    setSelectedGroup(null);
    setSelectedCampus(null);
  };

  const selectIntent = (i: Intent) => {
    setIntent(i);
    setStep('group');
  };

  const selectGroup = (group: SmartGroup) => {
    setSelectedGroup(group);
    setStep('campus');
  };

  const selectCampus = (cs: CampusSession) => {
    setSelectedCampus(cs);
    setStep('result');
  };

  const getResultURL = (cs: CampusSession, i: Intent) => {
    if (i === 'serving') return cs.Work_Crew_URL;
    if (i === 'leader') return cs.Leader_URL ?? cs.Registration_URL;
    return cs.Registration_URL;
  };

  const getResultLabel = (i: Intent) => {
    if (i === 'serving') return 'Join Work Crew';
    if (i === 'leader') return 'Register as Leader';
    return 'Register';
  };

  const getResultIcon = (i: Intent) => {
    if (i === 'serving') return HandHeart;
    if (i === 'leader') return Shield;
    return GraduationCap;
  };

  const slideVariants = {
    enter: { x: 40, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -40, opacity: 0 },
  };

  return (
    <div className="overflow-hidden">
      <AnimatePresence mode="wait">
        {/* Step 1: Student, Leader, or Work Crew? */}
        {step === 'intent' && (
          <motion.div
            key="intent"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-2">
              {(['student', 'leader', 'serving'] as const).map((key) => {
                const cfg = intentConfig[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectIntent(key)}
                    className="border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <cfg.Icon className="h-5 w-5 text-[var(--brand-secondary)]" />
                      <div>
                        <p className="text-foreground text-sm font-bold">{cfg.label}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">{cfg.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Step 2: Pick group (MS or HS) */}
        {step === 'group' && intent && (
          <motion.div
            key="group"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              onClick={() => setStep('intent')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {intentConfig[intent].label}
            </button>

            <div className="space-y-2">
              {registration.Groups.map((group) => (
                <button
                  key={group.Label}
                  type="button"
                  onClick={() => selectGroup(group)}
                  className="border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
                >
                  <div>
                    <p className="text-foreground text-sm font-bold">{group.Label}</p>
                    {group.Description && (
                      <p className="text-muted-foreground mt-0.5 text-xs">{group.Description}</p>
                    )}
                  </div>
                  <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 3: Pick campus */}
        {step === 'campus' && selectedGroup && (
          <motion.div
            key="campus"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              onClick={() => setStep('group')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {selectedGroup.Label}
            </button>

            <div className="space-y-2">
              {selectedGroup.Campus_Sessions.map((cs) => (
                <button
                  key={cs.Campus}
                  type="button"
                  onClick={() => selectCampus(cs)}
                  className="border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
                >
                  <div>
                    <p className="text-foreground flex items-center gap-1.5 text-sm font-bold">
                      <MapPin className="h-3.5 w-3.5 text-[var(--brand-secondary)]" />
                      {cs.Campus}
                    </p>
                    <p className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                      <Calendar className="h-3 w-3" />
                      {cs.Dates}
                    </p>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 4: Result */}
        {step === 'result' && intent && selectedGroup && selectedCampus && (
          <motion.div
            key="result"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              onClick={() => setStep('campus')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {selectedCampus.Campus}
            </button>

            <div className="border-border bg-background border p-4">
              <p className="text-foreground text-sm font-bold">{selectedCampus.Session_Label}</p>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {selectedCampus.Campus}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {selectedCampus.Dates}
                </span>
              </div>

              <div className="mt-4">
                {(() => {
                  const url = getResultURL(selectedCampus, intent);
                  const label = getResultLabel(intent);
                  const ResultIcon = getResultIcon(intent);

                  if (!url) {
                    return (
                      <p className="text-muted-foreground py-2 text-center text-sm">
                        Sign-up is not yet available for this session.
                      </p>
                    );
                  }

                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 bg-[var(--brand-secondary)] px-5 py-3 text-sm font-bold tracking-wide text-white uppercase transition-opacity hover:opacity-90 active:scale-[0.98]"
                    >
                      <ResultIcon className="h-4 w-4" />
                      {label}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  );
                })()}
              </div>
            </div>

            <button
              type="button"
              onClick={reset}
              className="text-muted-foreground hover:text-foreground mt-3 text-xs font-medium underline underline-offset-2 transition-colors"
            >
              Choose a different option
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
